import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Form, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { articleService, UPLOADS_BASE_URL } from '../services/api';
import { FaClock, FaEye } from 'react-icons/fa';
import NewsletterSignup from '../components/NewsletterSignup';
import SEO from '../components/SEO';
import EmptyState from '../components/EmptyState';
import { SkeletonGrid } from '../components/SkeletonLoader';
import './BlogPage.css';

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const categoryLabels = {
    'dive-sites': 'Dive Sites',
    'trip-reports': 'Trip Reports',
    'gear-reviews': 'Gear Reviews',
    'techniques': 'Diving Techniques',
    'safety': 'Safety',
    'beginner-guides': 'Beginner Guides',
    'destinations': 'Destinations',
    'marine-life': 'Marine Life',
    'conservation': 'Conservation',
    'news': 'News'
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadArticles();
  }, [page, selectedCategory, searchQuery]);

  const loadCategories = async () => {
    try {
      const response = await articleService.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 9
      };
      
      if (selectedCategory) {
        params.category = selectedCategory;
      }
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const response = await articleService.getArticles(params);
      setArticles(response.data.articles);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadArticles();
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setPage(1);
  };

  const handleReset = () => {
    setSelectedCategory('');
    setSearchQuery('');
    setPage(1);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="blog-page">
      <SEO 
        title="Dive Blog - Scuba Diving Guides, Tips & Destination Reviews | DiveConnect"
        description="Explore comprehensive scuba diving guides, destination reviews, gear tips, and underwater photography advice. Learn from expert divers and discover your next diving adventure."
        keywords="scuba diving blog, diving guides, dive site reviews, diving tips, underwater photography, diving gear reviews, dive destinations, diving techniques, marine life"
        url="https://dive-connect.com/blog"
      />
      
      {/* Hero Section */}
      <div className="blog-hero">
        <Container>
          <h1>Dive Blog</h1>
          <p className="lead">Explore diving destinations, tips, and stories from around the world</p>
        </Container>
      </div>

      <Container className="my-5">
        <Row>
          {/* Sidebar */}
          <Col lg={3} className="mb-4">
            <div className="blog-sidebar">
              {/* Search */}
              <Card className="mb-4">
                <Card.Body>
                  <h5>Search Articles</h5>
                  <Form onSubmit={handleSearch}>
                    <Form.Group className="mb-2">
                      <Form.Control
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </Form.Group>
                    <Button type="submit" variant="primary" size="sm" className="w-100">
                      Search
                    </Button>
                  </Form>
                </Card.Body>
              </Card>

              {/* Categories */}
              <Card>
                <Card.Body>
                  <h5>Categories</h5>
                  <div className="category-list">
                    <Button
                      variant={selectedCategory === '' ? 'primary' : 'outline-primary'}
                      size="sm"
                      className="w-100 mb-2 text-start"
                      onClick={() => handleCategoryChange('')}
                    >
                      All Articles
                      {categories.length > 0 && (
                        <Badge bg="secondary" className="float-end">
                          {categories.reduce((sum, cat) => sum + cat.count, 0)}
                        </Badge>
                      )}
                    </Button>
                    {categories.map((category) => (
                      <Button
                        key={category.slug}
                        variant={selectedCategory === category.slug ? 'primary' : 'outline-primary'}
                        size="sm"
                        className="w-100 mb-2 text-start"
                        onClick={() => handleCategoryChange(category.slug)}
                      >
                        {category.name}
                        <Badge bg="secondary" className="float-end">
                          {category.count}
                        </Badge>
                      </Button>
                    ))}
                  </div>
                </Card.Body>
              </Card>

              {/* Newsletter Signup */}
              <div className="mt-4">
                <NewsletterSignup />
              </div>

              {/* Reset Filters */}
              {(selectedCategory || searchQuery) && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="w-100 mt-3"
                  onClick={handleReset}
                >
                  Reset Filters
                </Button>
              )}
            </div>
          </Col>

          {/* Articles Grid */}
          <Col lg={9}>
            {loading ? (
              <SkeletonGrid count={9} type="article" />
            ) : articles.length === 0 ? (
              <EmptyState 
                type="search"
                title="No Articles Found"
                message={selectedCategory || searchQuery 
                  ? "We couldn't find any articles matching your search. Try different keywords or browse all articles."
                  : "No articles published yet. Check back soon for diving tips, guides, and destination reviews!"
                }
                actionLabel={selectedCategory || searchQuery ? "Clear Filters" : "Explore Trips"}
                onAction={selectedCategory || searchQuery ? handleReset : () => window.location.href = '/trips'}
              />
            ) : (
              <>
                <Row>
                  {articles.map((article) => (
                    <Col key={article._id} md={6} lg={4} className="mb-4">
                      <Card className="article-card h-100">
                        <Card.Img
                          variant="top"
                          src={`${UPLOADS_BASE_URL}/uploads/${article.featuredImage}`}
                          alt={article.title}
                          onError={(e) => {
                            e.target.src = `${UPLOADS_BASE_URL}/uploads/default-article.jpg`;
                          }}
                        />
                        <Card.Body className="d-flex flex-column">
                          <div className="mb-2">
                            <Badge bg="info" className="me-2">
                              {categoryLabels[article.category]}
                            </Badge>
                          </div>
                          <Card.Title>
                            <Link to={`/blog/${article.slug}`} className="article-title-link stretched-link">
                              {article.title}
                            </Link>
                          </Card.Title>
                          <Card.Text className="text-muted small flex-grow-1">
                            {article.excerpt}
                          </Card.Text>
                          <div className="article-meta">
                            <small className="text-muted">
                              <FaClock className="me-1" />
                              {article.readTime} min read
                            </small>
                            <small className="text-muted ms-3">
                              <FaEye className="me-1" />
                              {article.views} views
                            </small>
                          </div>
                          <div className="article-date mt-2">
                            <small className="text-muted">
                              {formatDate(article.publishedAt)}
                            </small>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <Button
                      variant="outline-primary"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="me-2"
                    >
                      Previous
                    </Button>
                    <div className="d-flex align-items-center mx-3">
                      <span>Page {page} of {totalPages}</span>
                    </div>
                    <Button
                      variant="outline-primary"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BlogPage;
